import { Asset } from '@/api/generatedApi';
import { AssetNameWithExtension } from '@/components/AssetNameWithExtension';
import { BodyText } from '@/components/BodyText';
import { Thumbnail } from '@/components/Thumbnail';
import { ThumbnailPreview } from '@/components/ThumbnailPreview';
import { getAssetName } from '@/utils/assets';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { getFileExtension } from '@physna/supported-files';
import { Link } from 'react-router-dom';
import { AssetMatch, BulkUploadData } from '../types';

const getAssetById = (assetId: string, data: BulkUploadData) => {
  const match = data.assetMatches.find(match => match.selectedAsset?.id === assetId);
  return match?.selectedAsset;
};

export const RenderAssetNameWithThumbnail = (
  parameters: GridRenderCellParams,
  getAssetUrl: (assetId: string) => string,
  data: BulkUploadData
) => {
  const asset = getAssetById(parameters.row.assetId, data);
  const assetName = parameters.row.assetName;
  const assetId = parameters.row.assetId;

  return asset ? (
    <Link
      to={getAssetUrl(assetId)}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={event => event.stopPropagation()}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
        <ThumbnailPreview primaryAsset={asset}>
          <Box sx={{ cursor: 'pointer' }}>
            <Thumbnail size={42} asset={asset} noLink={true} />
          </Box>
        </ThumbnailPreview>
        <AssetNameWithExtension
          name={getAssetName(asset)}
          path={asset.path}
          extension={getFileExtension(asset.path)}
          showAssemblyPill={asset.isAssembly}
          isRootAssembly={asset.isRootAssembly}
          isMissingDependencies={asset.isMissingDependencies}
        />
      </Box>
    </Link>
  ) : (
    <Link
      to={getAssetUrl(assetId)}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onClick={event => event.stopPropagation()}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
        <Thumbnail size={42} asset={{ id: assetId, path: assetName }} noLink={true} />
        <AssetNameWithExtension
          name={assetName.slice(0, -getFileExtension(assetName).length)}
          path={assetName}
          extension={getFileExtension(assetName)}
          showAssemblyPill={false}
        />
      </Box>
    </Link>
  );
};

export const RenderMetadataUpdates = (updatedFields: Record<string, string>) => {
  const entries = Object.entries(updatedFields);

  if (entries.length === 0) {
    return <BodyText color='textSecondary'>-</BodyText>;
  }

  const maxVisible = 2;
  const visibleEntries = entries.slice(0, maxVisible);
  const hasMore = entries.length > maxVisible;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        flexWrap: 'nowrap',
        width: '100%',
        height: '100%',
        minHeight: '32px',
        overflow: 'hidden',
      }}
    >
      {visibleEntries.map(([field, value]) => (
        <Chip key={field} label={`${field}: ${value}`} size='small' variant='outlined' sx={{ flexShrink: 0 }} />
      ))}
      {hasMore && (
        <Tooltip
          title={
            <Box>
              {entries.slice(maxVisible).map(([field, value]) => (
                <BodyText key={field}>
                  {field}: {value}
                </BodyText>
              ))}
            </Box>
          }
        >
          <Chip
            label={`+${entries.length - maxVisible} more`}
            size='small'
            variant='outlined'
            color='default'
            sx={{ flexShrink: 0 }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export const RenderErrorMessage = (errorMessage: string) => <BodyText color='error'>{errorMessage}</BodyText>;

export const RenderStatusChip = (status: 'success' | 'failed') => (
  <Chip
    icon={status === 'success' ? <CheckCircle /> : <ErrorIcon />}
    label={status === 'success' ? 'Success' : 'Failed'}
    color={status === 'success' ? 'success' : 'error'}
    size='small'
  />
);

export const RenderAssetMatchingStatus = (
  parameters: GridRenderCellParams,
  getStatusIcon: (status: AssetMatch['status']) => React.ReactNode,
  getStatusText: (match: AssetMatch) => string
) => {
  const match = parameters.row as AssetMatch;
  return (
    <Box display='flex' alignItems='center'>
      {getStatusIcon(match.status)}
      {getStatusText(match)}
    </Box>
  );
};

export const RenderAssetMatchingAction = (
  parameters: GridRenderCellParams,
  openAssetSelectionDialog: (assetName: string, matches: Asset[], currentSelection?: Asset) => void
) => {
  const match = parameters.row as AssetMatch;
  return match.status === 'multiple_matches' || (match.status === 'matched' && match.searchResults.length > 1) ? (
    <Button
      size='small'
      variant='outlined'
      onClick={() => openAssetSelectionDialog(match.assetName, match.searchResults, match.selectedAsset)}
    >
      {match.status === 'multiple_matches' ? 'Select Asset' : 'Change Asset'}
    </Button>
  ) : undefined;
};

export const RenderConfirmationMetadataUpdates = (parameters: GridRenderCellParams, data: BulkUploadData) => {
  const row = data.parsedData.find(r => r.assetName === parameters.row.assetName);
  if (!row) return '-';
  return RenderMetadataUpdates(row.metadata);
};

export const RenderDetails = (parameters: GridRenderCellParams) =>
  parameters.row.status === 'success'
    ? RenderMetadataUpdates(parameters.row.updatedFields)
    : RenderErrorMessage(parameters.row.errorMessage);
